/**
 * Speak the work, and AI drafts this line's scope of work.
 *
 * Jonathon, 2026-08-24, after watching Samuel build estimates — and then,
 * narrowing it: "the ai feature i simply want on the note section of each
 * line item." So this sits under the Note box in the line editor and does
 * exactly one thing: the rep talks through the work (the browser's own
 * dictation — free, and typing works everywhere it is missing), AI writes
 * the scope, and the draft lands in the note box for the rep to edit like
 * anything else they typed there.
 *
 * What it writes is a whole scope of work, not a tidy-up of the sentence the
 * rep gave it (Jonathon, 2026-09-06: "i want it to act like chat gpt where i
 * type out or say i need a scope of work for xyz and it actually types out a
 * detailed scope of work"). The rep names the job; the prompt in
 * `supabase/functions/scope-draft/request.ts` writes out the steps the trade
 * customarily includes, in the house's numbered-section shape. Which is why
 * the box below asks for the job rather than for the wording.
 *
 * The AI writes words, only words, for this one line. No prices, no dates and
 * no measurement the rep did not give it — the prompt forbids all three, and
 * pricing stays the line's own money fields. The parent saves the drafted
 * note the moment it lands (ADR 0048: editors save as you go; a drafted scope
 * must not evaporate because nobody clicked out of the box), and every edit
 * after that saves the way the note always has.
 */

import { useEffect, useRef, useState } from "react";

import { t } from "../../i18n";
import { ScopeDraftError, draftScopeNote } from "../../lib/scopeDraft";

/** The Web Speech API, as much of it as this needs. */
interface Dictation {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult:
    | ((event: {
        resultIndex: number;
        results: ArrayLike<
          ArrayLike<{ transcript: string }> & { isFinal: boolean }
        >;
      }) => void)
    | null;
  onend: (() => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  start(): void;
  stop(): void;
}

type DictationWindow = Window & {
  SpeechRecognition?: new () => Dictation;
  webkitSpeechRecognition?: new () => Dictation;
};

/**
 * What to tell the rep when dictation refuses.
 *
 * This used to be nothing at all: `onerror` reset the button and said not one
 * word, so a refused microphone looked exactly like a broken feature — which
 * is how it was reported, on 2026-09-22, and it had been refused for every rep
 * since the feature shipped (ADR 0196). A cause the rep can act on is the
 * difference between "it's not working" and "let it use the microphone".
 *
 * `aborted` is us stopping it and `no-speech` is silence; neither is a fault,
 * and the box is there to type in either way.
 */
const DICTATION_TROUBLE: Record<string, string> = {
  "not-allowed":
    "The browser is not letting this page use the microphone. Allow it for this site, then try again — or just type the job below.",
  "service-not-allowed":
    "This browser's dictation service is unavailable. Type the job below instead — the draft is exactly the same.",
  "audio-capture":
    "No microphone was found. Type the job below instead — the draft is exactly the same.",
  network:
    "Dictation could not reach the internet. Check the signal, or type the job below.",
};

const dictationCtor = (): (new () => Dictation) | null => {
  if (typeof window === "undefined") return null;
  const w = window as DictationWindow;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

export function ScopeNoteAssist({
  lineName,
  trade,
  onDraft,
}: {
  /** The line's heading, for the model's context. */
  lineName: string | null;
  /** The line's trade, likewise. */
  trade: string | null;
  /**
   * Receives the drafted scope and the protective clauses it calls for; the
   * parent puts both on the line and saves (ADR 0100).
   */
  onDraft: (note: string, qualifiers: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trouble, setTrouble] = useState<string | null>(null);

  const recognition = useRef<Dictation | null>(null);
  const canDictate = dictationCtor() !== null;

  // A closed editor must not keep the microphone open.
  useEffect(() => () => recognition.current?.stop(), []);

  function stopListening() {
    recognition.current?.stop();
    recognition.current = null;
    setListening(false);
    setInterim("");
  }

  function startListening() {
    const Ctor = dictationCtor();
    if (!Ctor) return;
    setTrouble(null);
    const mic = new Ctor();
    mic.continuous = true;
    mic.interimResults = true;
    mic.lang = "en-US";
    mic.onresult = (event) => {
      let pending = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]!;
        const words = result[0]?.transcript ?? "";
        if (result.isFinal) {
          const said = words.trim();
          if (said) {
            setTranscript((prior) => (prior ? `${prior} ${said}` : said));
          }
        } else {
          pending += words;
        }
      }
      setInterim(pending);
    };
    // The platform ends dictation on its own after silence; the button must
    // not go on claiming to listen when nothing is.
    mic.onend = () => {
      setListening(false);
      setInterim("");
    };
    mic.onerror = (event) => {
      setListening(false);
      setInterim("");
      const reason = event?.error ?? "";
      if (reason === "aborted" || reason === "no-speech") return;
      const why = DICTATION_TROUBLE[reason];
      setTrouble(
        why
          ? t(why)
          : t(
              "Dictation stopped unexpectedly. Type the job below instead — the draft is exactly the same.",
            ),
      );
    };
    recognition.current = mic;
    // A browser that refuses at `start` throws rather than calling `onerror`,
    // and an exception out of a click handler is a button that does nothing.
    try {
      mic.start();
    } catch {
      recognition.current = null;
      setTrouble(
        t(
          "Dictation would not start. Type the job below instead — the draft is exactly the same.",
        ),
      );
      return;
    }
    setListening(true);
  }

  async function generate() {
    stopListening();
    setBusy(true);
    setError(null);
    try {
      const { note, qualifiers } = await draftScopeNote(transcript, {
        lineName,
        trade,
      });
      onDraft(note, qualifiers);
      // Ready for the next line: the words served their purpose.
      setTranscript("");
      setOpen(false);
    } catch (cause) {
      setError(
        cause instanceof ScopeDraftError
          ? cause.message
          : t("Could not draft the scope. Try again."),
      );
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn-small"
        onClick={() => setOpen(true)}
        style={{ marginTop: 6 }}
      >
        {t("🎤 Draft with AI")}
      </button>
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      <textarea
        rows={4}
        aria-label={t("Describe the work for the AI draft")}
        placeholder={t(
          "Say what the job is — “full architectural tear-off with ridge vent” is enough — and AI writes the whole scope of work above for you to edit. It replaces what's in the box.",
        )}
        value={interim ? `${transcript} ${interim}`.trim() : transcript}
        onChange={(e) => {
          setTranscript(e.target.value);
          setInterim("");
        }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        {canDictate ? (
          <button
            type="button"
            className="btn-small"
            onClick={() => (listening ? stopListening() : startListening())}
          >
            {listening ? t("◼ Stop Listening") : t("🎤 Start Talking")}
          </button>
        ) : (
          <span className="na-hint">
            {t("This browser has no dictation — type it instead, same result.")}
          </span>
        )}
        <button
          type="button"
          className="btn-small"
          disabled={busy || !transcript.trim()}
          onClick={() => void generate()}
        >
          {busy ? t("Writing the scope…") : t("Write the Scope")}
        </button>
        <button
          type="button"
          className="btn-small"
          disabled={busy}
          onClick={() => {
            stopListening();
            setOpen(false);
          }}
        >
          {t("Cancel")}
        </button>
      </div>
      {listening && (
        <div className="na-hint" role="status">
          {t("Listening… it stops on its own when you go quiet.")}
        </div>
      )}
      {trouble && (
        <p className="err" role="status">
          {trouble}
        </p>
      )}
      {error && <p className="err">{error}</p>}
    </div>
  );
}
